import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, FlowArrow, Info, Question, ShieldWarning, UserCircleCheck } from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { ladeSchalter } from '../../../lib/vorgaenge/funktionen';
import { ladeVorgangsarten, type Vorgangsart } from '../../../lib/vorgaenge/vorgangsarten';
import { bewerteAngaben, rueckfragen, type AngabeZeile, type Bekannt } from '../../../lib/vorgaenge/start';
import { zaehleChecklistenFortschritt } from '../../../lib/tickets/checklistenVorlagen';
import type { VorgangStart } from '../../../lib/ki/erstelleTicketAusAnalyse';

const STATUS: Record<AngabeZeile['status'], { icon: typeof CheckCircle; farbe: string; text: string }> = {
  mail: { icon: CheckCircle, farbe: 'var(--ok)', text: 'aus der Mail' },
  app: { icon: UserCircleCheck, farbe: 'var(--ok)', text: 'kennt die App' },
  intern: { icon: Info, farbe: 'var(--ink3)', text: 'prüft das Team' },
  fehlt: { icon: Question, farbe: 'var(--warn)', text: 'wird erfragt' },
};

/**
 * #489 Etappe V3: „Vorgang starten“ in der KI-Analyse einer Mail. Nur sichtbar, wenn der
 * Schalter „Vorgang aus der Mail starten“ an ist. Zeigt die erkannte Vorgangsart (änderbar),
 * die Angaben (vorhanden / bekannt / fehlt), Checkliste und Fristen; Start legt das Ticket an.
 */
export default function VorgangStartKarte({ erkannt, ausMail, bekannt, laeuft, onStart, mobil = false }: {
  erkannt: string | null;
  ausMail: Record<string, string> | null;
  bekannt: Bekannt;
  laeuft: boolean;
  onStart: (vorgang: VorgangStart) => void;
  mobil?: boolean;
}) {
  const [aktiv, setAktiv] = useState(false);
  const [arten, setArten] = useState<Vorgangsart[]>([]);
  const [wahl, setWahl] = useState<string>(erkannt ?? '');
  const [checkliste, setCheckliste] = useState<{ id: string; name: string; text: string } | null>(null);

  useEffect(() => {
    let weg = false;
    void (async () => {
      try {
        const [s, a] = await Promise.all([ladeSchalter(), ladeVorgangsarten(true)]);
        if (weg) return;
        setAktiv(s.startkarte);
        setArten(a);
      } catch (e) { console.warn('Vorgänge laden fehlgeschlagen', e); }
    })();
    return () => { weg = true; };
  }, []);
  useEffect(() => { setWahl(erkannt ?? ''); }, [erkannt]);

  const art = arten.find(a => a.id === wahl) ?? null;
  useEffect(() => {
    if (!art?.checkliste_vorlage_id) { setCheckliste(null); return; }
    let weg = false;
    supabase.from('ticket_checklisten_vorlagen').select('id, name, checkliste').eq('id', art.checkliste_vorlage_id).maybeSingle()
      .then(({ data }) => { if (!weg) setCheckliste(data ? { id: data.id, name: data.name, text: data.checkliste } : null); });
    return () => { weg = true; };
  }, [art?.checkliste_vorlage_id]);

  // KI-Angaben gehören nur zur erkannten Art
  const zeilen = useMemo(() => (art ? bewerteAngaben(art, art.id === erkannt ? ausMail : null, bekannt) : []), [art, erkannt, ausMail, bekannt]);
  const fragen = rueckfragen(zeilen);

  if (!aktiv || arten.length === 0) return null;

  const auswahl = (
    <select value={wahl} onChange={e => setWahl(e.target.value)} aria-label="Vorgangsart"
      style={{ height: mobil ? 44 : 32, padding: '0 10px', borderRadius: 10, border: 'none', outline: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 12, fontWeight: 700, fontFamily: 'inherit', maxWidth: '100%' }}>
      <option value="">{erkannt ? 'Kein Vorgang' : 'Vorgangsart wählen …'}</option>
      {arten.map(a => <option key={a.id} value={a.id}>{a.name}{a.id === erkannt ? ' (erkannt)' : ''}</option>)}
    </select>
  );

  // Nichts erkannt und nichts gewählt: nur die kompakte Auswahl
  if (!art) {
    return (
      <div className="flex items-center flex-wrap" style={{ gap: 8, fontSize: 12, color: 'var(--ink2)' }}>
        <FlowArrow size={14} weight="bold" color="var(--acc)" /> Als Vorgang starten: {auswahl}
      </div>
    );
  }

  const ohneAntwort = art.rechtlich_sensibel || art.freigabe_stufe === 'vorschlag';
  const punkte = checkliste ? zaehleChecklistenFortschritt(checkliste.text).gesamt : 0;

  return (
    <div className="flex flex-col" style={{ gap: 10, padding: 12, borderRadius: 16, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
      <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
        <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 800 }}>
          <FlowArrow size={16} weight="bold" color="var(--acc)" /> Vorgang
        </span>
        {auswahl}
      </div>
      {art.erklaerung && <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--ink2)' }}>{art.erklaerung}</p>}

      {zeilen.length > 0 && (
        <ul className="flex flex-col" style={{ margin: 0, padding: 0, listStyle: 'none', gap: 4 }}>
          {zeilen.map(z => {
            const st = STATUS[z.status];
            const Icon = st.icon;
            return (
              <li key={z.schluessel} className="flex items-start" style={{ gap: 6, fontSize: 12 }}>
                <Icon size={14} weight="fill" color={st.farbe} className="shrink-0" style={{ marginTop: 1 }} />
                <span className="min-w-0" style={{ overflowWrap: 'anywhere' }}>
                  <b>{z.bezeichnung}</b>{z.wert ? `: ${z.wert}` : ''} <span style={{ color: 'var(--ink3)' }}>· {st.text}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-col" style={{ gap: 3, fontSize: 12, color: 'var(--ink2)' }}>
        <span><b>Beim Start:</b> Ticket{checkliste ? ` mit Checkliste „${checkliste.name}“ (${punkte} Punkte)` : ''}
          {art.faellig_tage != null ? ` · Frist ${art.faellig_tage} Tage` : ''}{art.wiedervorlage_tage != null ? ` · Wiedervorlage in ${art.wiedervorlage_tage} Tagen` : ''}.</span>
        {ohneAntwort ? (
          <span className="flex items-center" style={{ gap: 5, color: 'var(--warn)' }}>
            <ShieldWarning size={13} weight="fill" /> {art.rechtlich_sensibel ? 'Rechtlich sensibel – keine Antwort wird vorbereitet, ein Admin entscheidet.' : 'Nur Vorschlag – keine Antwort wird vorbereitet.'}
          </span>
        ) : (
          <span>Antwortentwurf {fragen.length ? `mit ${fragen.length} Rückfrage${fragen.length === 1 ? '' : 'n'}` : 'als Eingangsbestätigung'} – wird erst nach deiner Freigabe im Ticket versendet.</span>
        )}
      </div>

      <button type="button" disabled={laeuft} onClick={() => onStart({ art, angaben: zeilen, checkliste: checkliste?.text ?? null })}
        className="flex items-center justify-center cursor-pointer"
        style={{ height: mobil ? 44 : 36, borderRadius: mobil ? 22 : 18, border: 'none', gap: 6, fontSize: 13, fontWeight: 800, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', opacity: laeuft ? 0.5 : 1 }}>
        <FlowArrow size={14} weight="bold" /> Vorgang „{art.name}“ starten
      </button>
    </div>
  );
}
